/**
 * Raising hiring for a store that was just added: whether it is offered, and how.
 *
 * Offered only right after a save that made a NEW store - Stores › Add New Store when the
 * server answered 201 (a save over an existing store code answers 200 and is never
 * offered), and Store Requests › Approve & create store - and only when:
 *   - the person may raise hiring here (the Raise switch is on, "Raise & cancel hiring", a
 *     1Office sign-in) - "Raise hiring for it" - or may only ask (the Stores tick without
 *     that row) - "Ask for hiring for it";
 *   - the store is Active, and its staffing status is not one the raise refuses without a
 *     tick (the API's STAFFING_HOLDS in hiring-link-raise.logic.ts).
 * Otherwise nothing is offered and the screen behaves as it did before. The Raise window it
 * opens has the reason fixed to New store; nowhere else does the web send that reason from.
 */
import { STAFFING_STATUS, STORE_STATUS } from '../../constants/storeStatus';

/** Staffing statuses that say "do not hire here", as the raise reads them. */
export const NEW_STORE_STAFFING_HOLDS: readonly string[] = [
  STAFFING_STATUS.HIRING_ON_HOLD,
  STAFFING_STATUS.HIRING_NOT_NEEDED,
  STAFFING_STATUS.TAGGED_INTERNAL_SHUFFLING,
];

/** 'raise' opens the Raise hiring window; 'ask' opens it in ask mode (a hiring request). */
export type NewStoreRaiseMode = 'raise' | 'ask';

const same = (a: unknown, b: string): boolean => String(a ?? '').trim().toLowerCase() === b.toLowerCase();

/** How hiring is offered for a store just added, or null when it is not offered at all. */
export const newStoreRaiseMode = ({ canRaise, canAsk, storeStatus, staffingStatus }: {
  /** The person may raise hiring here, as the hiring screens already decide it. */
  canRaise: boolean;
  /** The person may only ask for hiring here. */
  canAsk: boolean;
  storeStatus: string | null | undefined;
  staffingStatus: string | null | undefined;
}): NewStoreRaiseMode | null => {
  if (!canRaise && !canAsk) return null;
  if (!same(storeStatus, STORE_STATUS.ACTIVE)) return null;
  if (NEW_STORE_STAFFING_HOLDS.some((hold) => same(staffingStatus, hold))) return null;
  return canRaise ? 'raise' : 'ask';
};
